/**
 * Die Zeitachse des Startbilds — an EINER Stelle, in Millisekunden.
 *
 * ===========================================================================
 * WARUM DIE ZEITEN HIER STEHEN UND NICHT IM CSS
 * ===========================================================================
 *
 * Die Vorlage (`claude-design-concept/splash-screen/`) fährt ihre Choreografie
 * über eine eigene Laufzeit: `animations-v3.jsx` rechnet je Bild einen
 * Autorenzeitpunkt `T` aus und interpoliert jeden Wert daraus. Diese Anwendung
 * übernimmt die **Choreografie** und ausdrücklich **nicht** die Laufzeit —
 * keine `requestAnimationFrame`-Schleife, kein Babel im Browser, keine neue
 * Abhängigkeit für vier Sekunden Bild. Was bleibt, sind CSS-Keyframes; was
 * eine solche Umsetzung erfahrungsgemäss verliert, ist die **eine Uhr**: Die
 * Verzögerungen verteilen sich auf ein Dutzend Regeln, und wer die Gesamtdauer
 * ändert, ändert sie an elf Stellen und vergisst die zwölfte.
 *
 * Deshalb steht hier die Tabelle und dort nur die Bewegung. Jede Verzögerung
 * und jede Dauer dieses Bildes wird aus dieser Datei gerechnet und von
 * `SplashScreen.tsx` als `animation-delay`/`animation-duration` gesetzt;
 * `splash.css` kennt keine einzige Zeitangabe.
 *
 * ===========================================================================
 * DIE SKALIERUNG — UND DIE EINE BENANNTE ABWEICHUNG
 * ===========================================================================
 *
 * Die Vorlage ist auf **7,1 s** gesetzt (die vier `dur`-Werte in `OM_SCENES`,
 * `Splash Screen.dc.html`); ihre eigene README empfiehlt für den Alltag 2,5–3 s
 * und sagt dazu, die Choreografie skaliere mit. Der Auftraggeber hat **2,8 s**
 * entschieden. Jede Zeit dieser Datei ist deshalb der Zeitpunkt der Vorlage,
 * multipliziert mit `SKALA` — nichts ist von Hand nachgerechnet, und wer die
 * Gesamtdauer ändert, ändert genau eine Zahl.
 *
 * **Zwei Werte skalieren NICHT mit, und beide haben einen Grund:**
 *
 * 1. Der Versatz der Wortmarke bleibt bei **42 ms** je Buchstabe. Er ist keine
 *    Länge, sondern ein Rhythmus: Skaliert läge er bei 17 ms, und vierzehn
 *    Buchstaben in 230 ms sind kein Setzen mehr, sondern ein Aufblitzen.
 * 2. Das Ausblenden am Schluss dauert **400 ms** statt der skalierten 355 ms —
 *    die Zahl aus dem Auftrag, weil sie der Übergang IN die Anwendung ist und
 *    nicht mehr Teil des Bildes. Es endet auf die Millisekunde mit der
 *    Gesamtdauer; was danach käme, sähe niemand.
 *
 * ===========================================================================
 * WAS DIE VORLAGE HAT UND DIESE FASSUNG NICHT
 * ===========================================================================
 *
 * Die Vorlage läuft in einer Schleife und nimmt ihr Aufhellen zum Schluss
 * wieder zurück (`bgLift = handoff * (1 - fade)`), damit die Naht zum nächsten
 * Durchlauf nicht springt. Hier läuft sie **einmal**: Die Bühne hellt zur
 * Flächenfarbe der Anwendung auf und bleibt dort, während alles darüber
 * ausblendet. Genau das ist der Übergang, den der Auftrag verlangt.
 */

/** Die vier Szenen der Vorlage mit ihren Autorenlängen in Sekunden. */
const KONZEPT_SZENEN = [
  { name: 'Raster', sekunden: 1.1 },
  { name: 'Signet', sekunden: 1.9 },
  { name: 'Wortmarke', sekunden: 1.7 },
  { name: 'Uebergabe', sekunden: 2.4 },
] as const;

/** Die Autorendauer der Vorlage: die Summe der vier Szenen, in Sekunden. */
export const KONZEPT_GESAMTDAUER_S = 7.1;

/**
 * Die Gesamtdauer des Startbilds in Millisekunden — die EINE Zahl, an der
 * gedreht wird. Alles andere hängt daran.
 */
export const GESAMTDAUER_MS = 2800;

/** Der Faktor, mit dem jede Zeit der Vorlage auf diese Fassung fällt. */
const SKALA = GESAMTDAUER_MS / (KONZEPT_GESAMTDAUER_S * 1000);

/** Rechnet einen Zeitpunkt (oder eine Länge) der Vorlage in Millisekunden um. */
function skaliert(sekunden: number): number {
  return Math.round(sekunden * 1000 * SKALA);
}

/** Ein Einsatz — der skalierte Beginn einer Szene, in Millisekunden. */
export interface Szene {
  readonly name: string;
  /** Beginn, gemessen ab dem ersten Bild. */
  readonly abMs: number;
  /** Länge dieser Szene. */
  readonly dauerMs: number;
}

/**
 * Die vier Szenen mit skalierten Zeiten.
 *
 * **Die Einsätze werden aus der laufenden Summe der Autorenzeiten gerechnet,
 * nicht aus der Summe der skalierten Längen.** Der Unterschied ist eine
 * Millisekunde und trotzdem der Punkt: Vier einzeln gerundete Längen ergeben
 * 2799 oder 2801, und die Gesamtdauer wäre dann nicht mehr die Zahl, die oben
 * steht.
 */
export const SZENEN: readonly Szene[] = KONZEPT_SZENEN.map((szene, i) => {
  const autorenBeginn = KONZEPT_SZENEN.slice(0, i).reduce((summe, s) => summe + s.sekunden, 0);
  const abMs = skaliert(autorenBeginn);
  return {
    name: szene.name,
    abMs,
    dauerMs: skaliert(autorenBeginn + szene.sekunden) - abMs,
  };
});

/** Ein Takt: wann eine Bewegung beginnt und wie lange sie dauert. */
export interface Takt {
  readonly versatzMs: number;
  readonly dauerMs: number;
}

/** Baut einen Takt aus zwei Zeitpunkten der VORLAGE (in deren Sekunden). */
function takt(vonSekunden: number, bisSekunden: number): Takt {
  const versatzMs = skaliert(vonSekunden);
  return { versatzMs, dauerMs: skaliert(bisSekunden) - versatzMs };
}

/**
 * Die Takte der einzelnen Bewegungen, in der Reihenfolge, in der man sie
 * sieht. Die Zeitpunkte in Klammern sind die der Vorlage
 * (`splash-piece.jsx`), damit ein Abgleich ohne Rechnen möglich bleibt.
 */
export const TAKTE = {
  /** Das Punktraster wächst; je Punkt kommt sein eigener Nachlauf dazu. */
  punkt: takt(0, 0.5),
  /** Das Signet wächst auf seine Grösse (`Signet + 0.15` … `+ 1.3`). */
  aufrichten: takt(1.25, 2.4),
  /** Der Halo blüht auf (`+ 0.9` … `+ 1.8`). */
  halo: takt(2.0, 2.9),
  /** Ein Buchstabe der Wortmarke; der Versatz je Buchstabe steht unten. */
  buchstabe: takt(3.0, 3.48),
  /** Die Linie unter der Wortmarke läuft aus (`Wortmarke + 0.45` … `+ 1.25`). */
  linie: takt(3.45, 4.25),
  /** Die Fassungszeile darunter (`+ 0.65` … `+ 1.2`). */
  fassung: takt(3.65, 4.2),
  /** Die Begrüssung (`Uebergabe − 0.5` … `+ 0.2`). */
  begruessung: takt(4.2, 4.9),
  /** Die Bühne hellt zur Flächenfarbe der Anwendung auf (`Uebergabe` … `+ 0.7`). */
  aufhellen: takt(4.7, 5.4),
} as const;

/**
 * Die drei Balken des Signets wachsen nacheinander von links (`PF-signetbalken`,
 * 2026-10-15): zuerst die lange Zeile, dann die mittlere, dann die kurze —
 * die Reihenfolge der Geometrie in `FAME_SIGNET_BALKEN`.
 *
 * Sie lösen die frühere Kontur ab, die sich um den alten Pfad zeichnete und
 * danach füllte. **Nicht skaliert**, aus demselben Grund wie die Buchstaben:
 * Die Zeiten sind ein Rhythmus. Jeder Balken wächst 340 ms, der nächste setzt
 * 160 ms später ein — zusammen 660 ms, gerechnet ab dem Einsatz des Signets
 * (`startMs`), und damit fertig, bevor die Wortmarke beginnt (1153 ms gegen 1183 ms).
 */
export const BALKEN = {
  startMs: skaliert(1.25),
  dauerMs: 340,
  versatzMs: 160,
} as const;

/** Der Takt des Balkens mit der Nummer `i` (0 = oberster). */
export function balkenTakt(i: number): Takt {
  return { versatzMs: BALKEN.startMs + i * BALKEN.versatzMs, dauerMs: BALKEN.dauerMs };
}

/**
 * Der Versatz je Buchstabe der Wortmarke. **Nicht skaliert** — siehe die
 * Begründung im Kopf dieser Datei.
 */
export const BUCHSTABEN_VERSATZ_MS = 42;

/**
 * Das Ausblenden am Schluss: 400 ms, `ease-out`, endet exakt mit der
 * Gesamtdauer. **Nicht skaliert** — siehe die Begründung im Kopf dieser Datei.
 */
export const AUSBLENDEN_MS = 400;

/** Der Takt des Ausblendens: die letzten 400 ms der Zeitachse. */
export const AUSBLENDEN: Takt = {
  versatzMs: GESAMTDAUER_MS - AUSBLENDEN_MS,
  dauerMs: AUSBLENDEN_MS,
};

/**
 * Die ruhige Fassung für `prefers-reduced-motion: reduce`: ein Standbild aus
 * Signet, Wortmarke und Begrüssung, 600 ms lang, danach dasselbe Ausblenden —
 * aber **ohne** das Wegziehen nach oben. Wer Bewegung abbestellt hat, bekommt
 * eine Blende und keine Fahrt.
 */
export const STANDBILD_MS = 600;

/** Wie lange die ruhige Fassung insgesamt steht. */
export const STANDBILD_GESAMTDAUER_MS = STANDBILD_MS + AUSBLENDEN_MS;

/**
 * Die Beschleunigungskurven der Vorlage als CSS-Entsprechung.
 *
 * `animations-v3.jsx` kennt drei (`MOTION.enter/draw/pop`); das sind die
 * gängigen Penner-Kurven, und ihre `cubic-bezier`-Fassungen stehen hier, damit
 * niemand sie je Regel neu errät. `draw` (die Kontur, die sich zeichnete) ist
 * mit der Kontur entfallen (`PF-signetbalken`).
 */
export const KURVEN = {
  /** `Easing.easeOutCubic` — jedes Erscheinen. */
  hinein: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
  /** `Easing.easeOutBack` — die Punkte, die aufspringen. */
  aufspringen: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
} as const;

/** Ein Punkt des Rasters, mit fertiger Lage in Pixeln. */
export interface Punkt {
  readonly obenPx: number;
  readonly linksPx: number;
  readonly groessePx: number;
  /** Der nähere Punkt trägt den vollen Markenton, der fernere den blassen. */
  readonly nah: boolean;
  /** Deckkraft im Ruhezustand — das Raster ist Untergrund, nicht Bild. */
  readonly deckung: number;
  /** Nachlauf dieses Punktes gegenüber dem Beginn der Szene. */
  readonly versatzMs: number;
}

const SPALTEN = 11;
const ZEILEN = 7;
const ABSTAND_X = 28;
const ABSTAND_Y = 26;

/**
 * Das Punktraster, wörtlich aus `splash-piece.jsx` übernommen: Der Abstand zur
 * oberen rechten Ecke bestimmt Grösse, Deckkraft und Nachlauf, und was zu weit
 * weg liegt, entfällt ganz. Gerechnet wird das einmal beim Laden des Moduls
 * und nicht je Bild — es hängt an nichts, was sich ändern könnte.
 */
export const PUNKTE: readonly Punkt[] = (() => {
  const punkte: Punkt[] = [];
  for (let zeile = 0; zeile < ZEILEN; zeile += 1) {
    for (let spalte = 0; spalte < SPALTEN; spalte += 1) {
      const abstand = Math.hypot(
        (SPALTEN - 1 - spalte) / (SPALTEN - 1),
        zeile / (ZEILEN - 1),
      );
      const naehe = Math.max(0, 1 - abstand / 1.15);
      if (naehe < 0.05) continue;
      const groessePx = Number((3 + naehe * 12).toFixed(1));
      punkte.push({
        obenPx: Number((zeile * ABSTAND_Y + (18 - groessePx) / 2).toFixed(1)),
        linksPx: Number((spalte * ABSTAND_X + (18 - groessePx) / 2).toFixed(1)),
        groessePx,
        nah: naehe > 0.5,
        deckung: Number((0.18 + naehe * 0.5).toFixed(3)),
        versatzMs: TAKTE.punkt.versatzMs + skaliert((1 - naehe) * 0.55),
      });
    }
  }
  return punkte;
})();

/** Die Breite des Rasterblocks in Pixeln. */
export const RASTER_BREITE_PX = SPALTEN * ABSTAND_X;

/** Die Höhe des Rasterblocks in Pixeln. */
export const RASTER_HOEHE_PX = ZEILEN * ABSTAND_Y;

/** Die Wortmarke, Buchstabe für Buchstabe. */
export const WORTMARKE = 'FameAccounting';

/**
 * Die ersten vier Buchstaben stehen fett (700), der Rest normal (400) —
 * „**Fame**Accounting" nach der Markenregel des Markenpakets (`PF-signetbalken`).
 * Beide in der Schriftfarbe: Die frühere Zweifarbigkeit der Vorlage (Markenton
 * für den zweiten Teil) kennt das Markenpaket nicht.
 */
export const WORTMARKE_TRENNUNG = 4;
